import {Controller, useFormContext} from "react-hook-form";
import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Button,
} from "@mui/material";
import {ZyusetuFieldKeyFragmentFragment, ZyusetuFieldKeyType,} from "@/graphql/graphql";
import {FormSchema} from "@/components/pages/Zyusetu/ZyusetuEditorSection.tsx";
import {zyusetuValueIntoZodValue} from "@/hooks/useGetFirstContent";
import {useState} from "react";
import toast from 'react-hot-toast';

// 内部処理用の型定義
export type SelectManyWithCustomInputValue = {
  selected: string[];
  custom: string[];
};

interface ZyusetuSelectManyWithCustomInputFieldProps {
  fieldKey: ZyusetuFieldKeyFragmentFragment;
  onFocus: () => void;
  updateField: (
    keyId: string,
    keyName: string,
    // updateField に渡す value の型
    value: FormSchema<ZyusetuFieldKeyType.SelectManyWithCustomInput>[string],
  ) => Promise<void>;
  isAvailable: boolean;
  isDefaultValueDialog?: boolean;
}

export function ZyusetuSelectManyWithCustomInputField({
                                                        fieldKey,
                                                        isAvailable,
                                                        onFocus,
                                                        updateField,
                                                        isDefaultValueDialog = false,
                                                      }: ZyusetuSelectManyWithCustomInputFieldProps) {
  const {control} =
    useFormContext<FormSchema<ZyusetuFieldKeyType.SelectManyWithCustomInput>>();
  
  const [customInput, setCustomInput] = useState("");
  
  return (
    <Controller
      name={fieldKey.keyName}
      control={control}
      render={({field, formState: {errors}, fieldState: {isDirty}}) => {
        // field.valueから表示用の selectedValues (string[]) を計算
        const fieldValue = field.value; // react-hook-form からの値 ({ value: string[] | {selected: string[], custom: string[]}, ... })
        let selectedValues: string[] = [];
        if (fieldValue?.value) {
          if (Array.isArray(fieldValue.value)) {
            // value が string[] の場合 (初期状態など)
            selectedValues = fieldValue.value;
          } else if (typeof fieldValue.value === 'object' && fieldValue.value !== null && 'selected' in fieldValue.value && 'custom' in fieldValue.value) {
            // value が { selected: string[], custom: string[] } の場合
            // 型アサーションを追加して selected と custom にアクセス
            const objValue = fieldValue.value as SelectManyWithCustomInputValue;
            selectedValues = [...(objValue.selected ?? []), ...(objValue.custom ?? [])];
          }
        }
        // それ以外 (undefined, null など) の場合は空配列
        
        // エラーチェックのためのヘルパー関数
        const getErrorMessage = () => {
          try {
            return errors?.[fieldKey.keyName]?.value?.message;
          } catch (e) {
            return "";
          }
        };
        
        const addCustomValue = async () => {
          if (!customInput.trim()) return;
          
          // 既に選択されている値に含まれていないか確認
          if (!selectedValues.includes(customInput)) {
            try {
              const newValues = [...selectedValues, customInput];
              
              // react-hook-form の onChange には zodValue を渡す
              const zodValue = zyusetuValueIntoZodValue(fieldKey, newValues);
              field.onChange(zodValue);
              
              // バックエンドへの updateField には配列形式の値を渡す
              await updateField(
                fieldKey.id,
                fieldKey.keyName,
                {
                  // payloadId や templateDescription を維持しつつ、value を差し替え
                  ...field.value,
                  value: newValues // 配列形式で渡す
                }
              );
              
              setCustomInput("");
            } catch (error) {
              console.error("カスタム値の追加に失敗しました:", error);
            }
          } else {
            // 重複している場合のエラー表示
            toast.error("この値は既に追加されています");
          }
        }; // addCustomValue 関数の閉じ括弧
        
        return (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <FormControl
              sx={{flexGrow: 3, minWidth: '500px'}}
              error={!!getErrorMessage()}
            >
              <Select
                inputRef={field.ref}
                onFocus={onFocus}
                {...field}
                disabled={!isAvailable}
                value={selectedValues}
                size={"small"}
                multiple={true}
                onBlur={async () => {
                  if (getErrorMessage()) return;
                  if (!isDirty) return;
                  
                  // デフォルト値設定ダイアログ内の場合は、onBlurでの自動更新を行わない
                  // それ以外の場合は通常通り自動更新する
                  if (!isDefaultValueDialog && field.value?.value) {
                    const currentValues = Array.isArray(field.value.value) ? field.value.value : [field.value.value];
                    await updateField(fieldKey.id, fieldKey.keyName, {
                      ...field.value,
                      value: currentValues // 配列形式で渡す
                    });
                  }
                }}
                onChange={(e) => {
                  field.onChange(
                    zyusetuValueIntoZodValue(fieldKey, e.target.value ?? []),
                  );
                }}
                renderValue={(selected) => {
                  // selected が string[] であることを保証 (念のため)
                  const displayValues = Array.isArray(selected) ? selected : [];
                  
                  return (
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                      {displayValues.map((value) => {
                        // Chip の key と label が文字列であることを保証
                        const chipKey = String(value);
                        const chipLabel = String(value);
                        return (
                          <Chip
                            tabIndex={-1}
                            size={"small"}
                            key={chipKey}
                            label={chipLabel}
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                            }}
                            onDelete={async (event) => {
                              event.preventDefault(); // デフォルトの削除動作を防ぐ
                              event.stopPropagation();
                              
                              const updatedValues = selected.filter((v) => v !== value);
                              
                              // 先に react-hook-form の状態を更新
                              field.onChange(
                                zyusetuValueIntoZodValue(
                                  fieldKey,
                                  updatedValues,
                                ),
                              );
                              
                              // 更新後の値でバックエンドに通知
                              await updateField(
                                fieldKey.id,
                                fieldKey.keyName,
                                {
                                  ...field.value,
                                  value: updatedValues // 配列形式で渡す
                                }
                              );
                              
                              // コメントアウト部分を削除
                            }}
                          />
                        );
                      })}
                    </Box>
                  );
                }}
              >
                {fieldKey.items?.map((option) => (
                  <MenuItem key={`${fieldKey.id}-select-${option}`} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {getErrorMessage() &&
                <FormHelperText style={{color: "red"}}>
                  {getErrorMessage() || ""}
                </FormHelperText>}
            </FormControl>
            
            {/* カスタム入力フィールド */}
            <TextField
              size="small"
              sx={{width: '200px', flexShrink: 0}}
              placeholder="その他の値を入力"
              value={customInput}
              onChange={(e) => setCustomInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCustomValue();
                }
              }}
              disabled={!isAvailable}
            />
            <Button
              variant="contained"
              size="small"
              onClick={addCustomValue}
              // 追加ボタンクリック時にカスタム値を追加し、更新する
              disabled={!isAvailable || !customInput.trim()}
              sx={{height: '36px'}}
            >
              追加
            </Button>
          </Box>
        );
      }}
    />
  );
}